import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { SendOutlined } from '@ant-design/icons';
import { Modal, notification } from 'antd';

import AssigneeDialog from '@totem/components/surveyV2/instances/AssigneeDialog';
import DateDialog from '@totem/components/surveyV2/instances/DateDialog';
import HealthDialog from '@totem/components/surveyV2/instances/HealthDialog';
import SurveyInstanceContext from '@totem/components/surveyV2/instances/SurveyInstancesContext';
import {
  LinkResponse,
  SurveyInstanceConnectionInput,
  SurveyInstanceQueryResult,
} from '@totem/components/surveyV2/instances/types';
import { buildSurveyInstanceFilters } from '@totem/components/surveyV2/instances/utilities';
import {
  SurveyInstanceStatistics,
  SurveyQueryResult,
} from '@totem/components/surveyV2/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { V2_SURVEY_ENDPOINT } from '@totem/utilities/endpoints';
import { omitNilOrEmpty } from '@totem/utilities/objectUtilities';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

type Props = {
  children?: ReactNode;
  region?: string[];
  building?: string[];
};

const SurveyInstanceContainer = ({ children, region, building }: Props) => {
  const [input, updateInput] = useState<SurveyInstanceConnectionInput>({
    pageSize: 20,
    page: 1,
    sortField: 'name',
    sortDirection: '1',
    region: typeof region !== 'undefined' && region !== null ? region : [],
    building:
      typeof building !== 'undefined' && building !== null ? building : [],
    template: [],
    startDate: [],
    dueDate: [],
    submitted: [],
    isLaunched: [],
    health: [],
  });
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [refreshStatistics, setRefreshStatistics] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<SurveyQueryResult[]>([]);
  const [statistics, setStatistics] = useState<SurveyInstanceStatistics>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [action, setAction] = useState<string>('');
  const [selectedInstance, setSelectedInstance] =
    useState<SurveyQueryResult>(null);
  const [selectedForLaunch, setSelectedForLaunch] = useState<string[]>([]);
  const [isNotificationSending, setIsNotificationSending] = useState(false);
  const [isLinkSending, setIsLinkSending] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const setInput = (updated: Partial<SurveyInstanceConnectionInput>) => {
    updateInput(omitNilOrEmpty({ ...input, ...updated }));

    setRefreshData(true);
  };

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  const buildParameters = () => {
    const params: Params = {
      paging: {
        page: input.page,
        pageSize: input.pageSize,
      },
      sort: {
        field: input.sortField,
        direction: +input.sortDirection,
      },
      filters: buildSurveyInstanceFilters(input),
    };

    return params;
  };

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();

      fetch(`${V2_SURVEY_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: SurveyInstanceQueryResult) => {
          setData(result.surveys);

          setTotalRecords(result.paging.totalRecords);

          setRefreshStatistics(true);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  useEffect(() => {
    if (refreshStatistics) {
      setRefreshStatistics(false);

      const params: Params = buildParameters();

      fetch(`${V2_SURVEY_ENDPOINT}/statistics`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: SurveyInstanceStatistics) => {
          setStatistics(result);
        });
    }
  }, [refreshStatistics, getApiToken]);

  const sendNotification = useCallback(async (surveyId: string) => {
    if (isNotificationSending) {
      return;
    }
    setIsNotificationSending(true);

    fetch(`${V2_SURVEY_ENDPOINT}/instance/${surveyId}/notification`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        return CheckResponseShowError(res);
      })
      .then((success: boolean) => {
        setIsNotificationSending(false);
        if (success) {
          api.success({
            title: 'Notification Sent',
            description: 'Survey notification has been successfully sent.',
          });
        }
      });
  }, []);

  const sendGetLink = useCallback(async (surveyId: string) => {
    if (isLinkSending) {
      return;
    }
    setIsLinkSending(true);

    fetch(`${V2_SURVEY_ENDPOINT}/instance/${surveyId}/link`, {
      method: 'Get',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        CheckResponseShowError(res);
        return res.json();
      })
      .then((response: LinkResponse) => {
        setIsLinkSending(false);

        if (isNotNull(response) && response.link !== '') {
          navigator.clipboard.writeText(response.link).then(() => {
            api.success({
              title: 'Link Copied',
              description:
                'The link to the survey should be copied to your clipboard.',
            });
          });
        }
      });
  }, []);

  const handleAction = (newAction: string, item: SurveyQueryResult) => {
    switch (newAction) {
      case 'show_health_update_dialog':
        setAction(newAction);

        setSelectedInstance(item);
        break;
      case 'show_launch_dialog':
        setAction(newAction);

        setSelectedInstance(item);
        break;
      case 'copy-link':
        sendGetLink(item.survey.id);
        break;
      case 'resend_notification':
        Modal.confirm({
          content: `Send ${item.assignedUser.email} notification email with survey link?`,
          title: `Resend Notification?`,
          cancelText: 'No',
          okText: 'Yes, Send',
          okType: 'link',
          icon: <SendOutlined />,
          onOk: () => sendNotification(item.survey.id),
        });
        break;
      case 'toggle_select_for_launch':
        if (!selectedForLaunch.includes(item.survey.id)) {
          setSelectedForLaunch([...selectedForLaunch, item.survey.id]);
        } else {
          const filteredList = selectedForLaunch.filter(
            (chk) => chk !== item.survey.id,
          );
          setSelectedForLaunch(filteredList);
        }
        break;
      case 'show_assignment_dialog':
        setAction(newAction);

        setSelectedInstance(item);
        break;
      case 'show_start_date_dialog':
        setAction(newAction);

        setSelectedInstance(item);
        break;
      case 'show_due_date_dialog':
        setAction(newAction);

        setSelectedInstance(item);
        break;
      case 'refresh':
        setAction('');

        setSelectedInstance(null);

        setRefreshData(true);
        break;
      default:
        setAction(newAction);

        setSelectedInstance(item);
    }
  };

  return (
    <SurveyInstanceContext.Provider
      value={{
        input,
        setInput,
        loading: isLoading,
        setIsLoading,
        data,
        totalRecords,
        action,
        selectedInstance,
        onAction: handleAction,
        selectedForLaunch,
        setSelectedForLaunch,
        statistics,
        setStatistics,
      }}
    >
      <div>{children}</div>
      {action === 'show_health_update_dialog' && selectedInstance !== null && (
        <HealthDialog />
      )}
      {action === 'show_assignment_dialog' && selectedInstance !== null && (
        <AssigneeDialog />
      )}
      {(action === 'show_start_date_dialog' ||
        action === 'show_due_date_dialog') &&
        selectedInstance !== null && <DateDialog />}
      {contextHolder}
    </SurveyInstanceContext.Provider>
  );
};

export default SurveyInstanceContainer;
